import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import AdminPage from '../components/AdminShell';

// Every inbound contact request: prozpects.com website forms (contact, event
// notify, privacy request, MVP inquiry) and in-app partner inquiries. They are
// emailed to support@/sales@ as they arrive; this page is the record when an
// email is missed or lost. Staff only — rows carry minors' contact details.
// Backend: GET /api/admin/inquiries (backend/app/routers/admin_inquiries.py).

export const TYPE_LABELS = {
  web_contact: 'Contact form',
  web_notify: 'Event notify',
  web_privacy: 'Privacy request',
  web_mvp: 'MVP inquiry',
  partner_inquiry: 'Partner inquiry',
};

const PAGE_SIZE = 50;
// Only a plain address becomes a mailto link: partner-inquiry emails are not
// validated server-side, and `a@b.com?bcc=…&body=…` would pre-fill a hidden
// recipient in staff's mail client. Anything else renders as text.
const PLAIN_EMAIL = /^[A-Za-z0-9._%+'-]+@[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)+$/;
const PREVIEW_CHARS = 160;

// Server timestamps are UTC without a tz suffix; parse as UTC, show local.
const formatDateTime = (iso) => {
  if (!iso) return '';
  const d = new Date(/([zZ])|([+-]\d{2}:?\d{2})$/.test(iso) ? iso : `${iso}Z`);
  return `${d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}, `
    + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
};

function Message({ text }) {
  const [open, setOpen] = useState(false);
  if (text.length <= PREVIEW_CHARS) return <div style={{ whiteSpace: 'pre-wrap' }}>{text}</div>;
  return (
    <div style={{ whiteSpace: 'pre-wrap' }}>
      {open ? text : `${text.slice(0, PREVIEW_CHARS)}…`}
      {' '}
      <button type="button" className="adm-link" onClick={() => setOpen(!open)}>
        {open ? 'Show less' : 'Show all'}
      </button>
    </div>
  );
}

export default function AdminInquiriesPage() {
  const { API_URL } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [type, setType] = useState('');
  const [draft, setDraft] = useState('');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  // Each fetch takes a ticket; a response whose ticket is stale (the filter or
  // search changed while it was in flight) is dropped instead of rendered.
  const latest = useRef(0);

  const fetchPage = useCallback(({ offset = 0, append = false } = {}) => {
    const params = { limit: PAGE_SIZE, offset };
    if (type) params.type = type;
    if (query) params.q = query;
    const ticket = ++latest.current;
    if (append) setLoadingMore(true); else setLoading(true);
    setError('');
    return axios.get(`${API_URL}/admin/inquiries`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      params,
    })
      .then((res) => {
        if (ticket !== latest.current) return;
        setTotal(res.data.total || 0);
        setItems((prev) => (append ? [...prev, ...(res.data.items || [])] : (res.data.items || [])));
      })
      .catch((err) => {
        if (ticket === latest.current) setError(err.response?.data?.detail || 'Failed to load inquiries.');
      })
      .finally(() => {
        if (ticket === latest.current) { setLoading(false); setLoadingMore(false); }
      });
  }, [API_URL, type, query]);

  useEffect(() => { fetchPage(); }, [fetchPage]);

  const shown = `Showing ${items.length}${total > 0 ? ` of ${total}` : ''} inquiries`;
  const hasMore = items.length < total;

  return (
    <AdminPage active="inquiries" title="Inquiries" subtitle={shown}>
      <form
        className="adm-toolbar"
        onSubmit={(e) => { e.preventDefault(); setQuery(draft.trim()); }}
        style={{ gap: 8, flexWrap: 'wrap' }}
      >
        <select
          aria-label="Form"
          className="form-field__input"
          style={{ width: 'auto' }}
          value={type}
          onChange={(e) => setType(e.target.value)}
        >
          <option value="">All forms</option>
          {Object.entries(TYPE_LABELS).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
        <input
          type="search"
          aria-label="Search inquiries"
          className="form-field__input"
          style={{ width: 280 }}
          placeholder="Search name, email or message"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
        />
        <button type="submit" className="adm-btn">Search</button>
      </form>

      {error && <div className="adm-error">{error}</div>}

      {loading ? (
        <div className="adm__card"><div className="adm-loading">Loading inquiries…</div></div>
      ) : (
        <div className="adm__card adm__card--flush">
          <div className="adm-tablewrap">
            <table className="adm-table">
              <thead>
                <tr>
                  <th>Received</th>
                  <th>Form</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Message</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && (
                  <tr><td colSpan={5}><div className="adm-empty">No inquiries match.</div></td></tr>
                )}
                {items.map((i) => (
                  <tr key={i.id}>
                    <td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(i.created_at)}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{TYPE_LABELS[i.type] || i.type}</td>
                    <td>{i.name}</td>
                    <td>
                      {PLAIN_EMAIL.test(i.email)
                        ? <a className="adm-link" href={`mailto:${i.email}`}>{i.email}</a>
                        : i.email}
                    </td>
                    <td style={{ minWidth: 280 }}><Message text={i.message} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {hasMore && (
            <div className="adm-pager">
              <button
                type="button"
                className="adm-btn"
                onClick={() => fetchPage({ offset: items.length, append: true })}
                disabled={loadingMore}
              >
                {loadingMore ? 'Loading…' : `Load more (${total - items.length} remaining)`}
              </button>
            </div>
          )}
        </div>
      )}
    </AdminPage>
  );
}
